import angular, { IPromise } from 'angular';
import { IEquipe, IEquipesResponse, Contas } from './models/equipe.model';
import { IProcessoAvaliadorListResponse, IProcessoAvaliadorRequest, IProcessoAvaliadorResponse, IProcessoAvaliadorInteraction, IProcessoAvaliadorListApiResponse } from './models/processo-avaliador.model';

export class FluxoAprovacoesService {
    static $inject = ['$http', 'nsjRouting', '$rootScope', '$q'];

    public entities: IEquipe[] = [];
    public loaded = false;
    public constructors: any = {};
    public after: any = {};
    public filters: any = {};
    public loading_deferred: angular.IDeferred<any> | null = null;
    public loading_deferred_filter: angular.IDeferred<any> | null = null;
    public filter = '';
    public loadParams: any = {
        to_load: 3,
        busy: false,
        busySave: false,
        finished: false
    };
    private processoPadrao: IProcessoAvaliadorListResponse | null = null;
    private _loadPromise: angular.IPromise<IEquipe[]> | null = null;

    constructor(
        public $http: angular.IHttpService,
        public nsjRouting: any,
        public $rootScope: angular.IRootScopeService,
        public $q: angular.IQService,
    ) { }

    reload() {
        if (this.loading_deferred) {
            this.loading_deferred.resolve();
        }

        this._loadPromise = null;
        this.loadParams.finished = false;
        this.loadParams.to_load = 3;
        this.after = {};
        this.loaded = false;
        this.entities.length = 0;
        this.loadParams.busy = false;

        return this.load();
    }

    load(): IEquipe[] {
        if (!this.loadParams.busy && !this.loadParams.finished && this.loadParams.to_load > 0) {
            this.loadParams.busy = true;

            this._load(this.constructors, this.after, this.filter)
                .then((data) => {
                    if (data.length > 0) {
                        for (let i = 0; i < data.length; i++) {
                            this.entities.push(data[i]);
                        }
                    }
                    this.loadParams.finished = true;
                    this.$rootScope.$broadcast('equipes_list_finished', this.entities);
                    this.loaded = true;

                    this.loadParams.to_load--;
                })
                .catch((error: any) => {
                    if (error.xhrStatus !== 'abort') {
                        this.loadParams.finished = true;
                    }
                })
                .finally(() => {
                    if (this.loaded || this.loadParams.finished) {
                        this.loadParams.busy = false;
                    }
                });
        }

        return this.entities;
    }

    getContas(filter: string = '', extraFilters: any = {}): IPromise<Contas[]> {
        return this.$q((resolve, reject) => {
            this.$http<Contas[]>({
                method: 'GET',
                url: this.nsjRouting.generate('app_contas_get',
                    angular.extend({}, { 'filter': filter }, extraFilters),
                    true),
            })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((response) => {
                    reject(response);
                });
        });
    }

    search(filter?: any): angular.IHttpPromise<any> | IPromise<Contas[]> {
        let search = '';

        if (typeof filter !== 'undefined' && filter != null) {
            search = filter.search ? filter.search : '';

            Object.keys(this.filters).forEach((k): void => {
                delete this.filters[k];
            });

            for (const fil in filter.filters) {
                if (fil.indexOf('.') > -1) {
                    const filterSplit = fil.split('.');
                    this.filters[filterSplit[0]] = {};
                    this.filters[filterSplit[0]][filterSplit[1]] = filter.filters[fil];
                } else {
                    this.filters[fil] = filter.filters[fil];
                }
            }
        } else if (filter == null) {
            if (this.loading_deferred_filter) {
                this.loading_deferred_filter.resolve();
            }
            return this._applyFilter('', this._loadContas(this.constructors, {}, ''));
        }

        if (filter && filter.lookup) {
            if (this.loading_deferred_filter) {
                this.loading_deferred_filter.resolve();
            }
        }

        return this._applyFilter(search, this._loadContas(this.constructors, {}, search));
    }

    getProcessosAvaliadores(): IPromise<IProcessoAvaliadorListResponse[]> {
        return this.$q((resolve, reject) => {
            this.$http<IProcessoAvaliadorListApiResponse>({
                method: 'GET',
                url: this.nsjRouting.generate('workflow_simplificado_index_avaliadores', {}, true),
            })
                .then((response) => {
                    const results = (response.data.results || []).map((processo) => this.ensureInteracaoDefaults(processo));
                    const processoPadrao = results.find((processo) => processo && processo.processo === 'MEURH_SOLICITACOES_DEFAULT') || null;
                    this.setProcessoPadrao(processoPadrao || null);
                    resolve(results.filter((processo) => processo && processo.processo !== 'MEURH_SOLICITACOES_DEFAULT'));
                })
                .catch((response) => reject(response));
        });
    }

    getProcessoPadrao(): IProcessoAvaliadorListResponse | null {
        return this.processoPadrao ? angular.copy(this.processoPadrao) : null;
    }

    setProcessoPadrao(processo: IProcessoAvaliadorListResponse | null): void {
        if (!processo) {
            this.processoPadrao = null;
            return;
        }

        const copy = angular.copy(processo);
        this.processoPadrao = this.ensureInteracaoDefaults(copy);
    }

    createProcessoAvaliador(request: IProcessoAvaliadorRequest): IPromise<IProcessoAvaliadorResponse> {
        return this.$q((resolve, reject) => {
            this.$http<IProcessoAvaliadorResponse>({
                method: 'POST',
                url: this.nsjRouting.generate('workflow_simplificado_create_avaliadores', {}, true),
                data: request
            })
                .then((response) => resolve(response.data))
                .catch((response) => reject(response));
        });
    }

    updateProcessoAvaliador(guid: string, request: IProcessoAvaliadorRequest): IPromise<IProcessoAvaliadorResponse> {
        return this.$q((resolve, reject) => {
            this.$http<IProcessoAvaliadorResponse>({
                method: 'PUT',
                url: this.nsjRouting.generate('workflow_simplificado_update_avaliadores', { id: guid }, true),
                data: request
            })
                .then((response) => resolve(response.data))
                .catch((response) => reject(response));
        });
    }

    save(entity: any, autosave: boolean): angular.IPromise<any> {
        this.loadParams.busySave = true;

        return this._save(entity, autosave)
            .then((response: any) => {
                this.$rootScope.$broadcast('equipes_submitted', {
                    entity,
                    response,
                    autosave
                });

                return response;
            })
            .catch((response: any) => {
                this.$rootScope.$broadcast('equipes_submit_error', {
                    entity,
                    response,
                    autosave
                });

                return this.$q.reject(response);
            })
            .finally(() => {
                this.loadParams.busySave = false;
            });
    }

    private ensureInteracaoDefaults(processo: IProcessoAvaliadorListResponse | null): IProcessoAvaliadorListResponse | null {
        if (!processo) {
            return processo;
        }

        const interacao = (processo.interacao && typeof processo.interacao === 'object')
            ? processo.interacao
            : {} as IProcessoAvaliadorInteraction;

        const maybeEquipeEntity = processo.equipecliente_entity as any;
        const ativa = typeof interacao.ativa === 'boolean'
            ? interacao.ativa
            : !!processo.posaprovacao;

        const equipeId = interacao.equipeId
            || this.extractEquipeGuid(interacao.equipe)
            || this.extractEquipeGuid(maybeEquipeEntity)
            || this.extractEquipeGuid(processo.equipecliente)
            || null;

        if (!ativa) {
            processo.interacao = { ativa: false, equipe: null, equipeId: null };
            processo.posaprovacao = false;
            processo.equipecliente = null;
            processo.equipecliente_entity = null;
            return processo;
        }

        const equipe = (interacao.equipe && typeof interacao.equipe === 'object' ? interacao.equipe : null)
            || (maybeEquipeEntity && typeof maybeEquipeEntity === 'object' ? maybeEquipeEntity : null)
            || null;

        processo.interacao = {
            ativa: true,
            equipeId: equipeId || null,
            equipe,
        };

        processo.posaprovacao = ativa;
        processo.equipecliente = equipeId || null;
        processo.equipecliente_entity = equipe || null;

        if (processo.parametros && typeof processo.parametros === 'object') {
            processo.parametros = { ...processo.parametros };
        }

        return processo;
    }

    private extractEquipeGuid(source: any): string | null {
        if (!source) {
            return null;
        }

        if (typeof source === 'string') {
            return source;
        }

        if (typeof source !== 'object') {
            return null;
        }

        if (Object.prototype.hasOwnProperty.call(source, 'equipe')) {
            return source.equipe || null;
        }

        if (Object.prototype.hasOwnProperty.call(source, 'avaliador')) {
            return source.avaliador || null;
        }

        if (Object.prototype.hasOwnProperty.call(source, 'equipecliente')) {
            return source.equipecliente || null;
        }

        return null;
    }

    private _load(constructors: any, offset: any, filter: any): angular.IPromise<IEquipe[]> {
        this.loading_deferred = this.$q.defer();
        const hasGestor = this.entities.some(equipe => equipe.codigo === '__GESTOR__');
        if (this.entities.length > 0 && hasGestor) {
            if (constructors && constructors.includeGestor === false) {
                return this.$q.resolve(this.entities.filter(e => e.codigo !== '__GESTOR__'));
            }
            return this.$q.resolve(this.entities);
        }

        // reutiliza a requisição em andamento para evitar chamadas duplicadas à API
        // (cada mda-ui-select dispara _load() ao inicializar ou receber foco)
        if (this._loadPromise) {
            return this._loadPromise;
        }

        if (constructors && (constructors as any).includeGestor) {
            if (!this.entities.some(e => e.codigo === '__GESTOR__')) {
                this.entities.unshift({
                    equipe: null as any,
                    codigo: '__GESTOR__',
                    titulo: 'Gestor',
                    descricao: 'Gestor direto do colaborador da solicitação',
                    tenant: 0,
                    padrao: false,
                    mestre: false,
                    usuarios: 'Gestor direto do colaborador da solicitação'
                });
            }
        }

        this._loadPromise = this.$q((resolve, reject) => {
            this.$http<IEquipesResponse>({
                method: 'GET',
                url: this.nsjRouting.generate('workflow_simplificado_index_equipes',
                    angular.extend({}, constructors, { 'offset': offset, 'filter': filter }, this.filters),
                    true),
                timeout: this.loading_deferred!.promise
            })
                .then((response) => {
                    resolve(response.data.results);
                })
                .catch((response) => {
                    reject(response);
                });
        });

        this._loadPromise.finally(() => {
            this._loadPromise = null;
        });

        return this._loadPromise;
    }

    private _loadContas(constructors: any, _offset: any, filter: any): IPromise<Contas[]> {
        this.loading_deferred_filter = this.$q.defer();

        return this.$q((resolve: angular.IQResolveReject<any>, reject: angular.IQResolveReject<any>): void => {
            this.$http({
                method: 'GET',
                url: this.nsjRouting.generate('app_contas_get',
                    angular.extend({}, constructors, { 'filter': filter }, this.filters),
                    true),
                timeout: this.loading_deferred_filter!.promise
            })
                .then((response: any) => resolve(response.data))
                .catch((response: any) => reject(response));
        });
    }

    private _save(entity: any, autosave: boolean) {
        let method, url;
        if (entity.equipe as IEquipe) {
            method = 'PUT';
            url = this.nsjRouting.generate('workflow_simplificado_update_equipe', { id: entity.equipe }, true);
        } else {
            method = 'POST';
            url = this.nsjRouting.generate('workflow_simplificado_create_equipe', {}, true);
        }
        const data = angular.copy(entity);
        this.loading_deferred = this.$q.defer();

        return this.$http({
            method,
            url,
            data,
            timeout: this.loading_deferred.promise
        });
    }

    private _applyFilter(search: string, promise: IPromise<Contas[]>): IPromise<Contas[]> {
        if (!search) {
            return promise;
        }

        const normalisedSearch = search.trim().toLowerCase();

        return promise.then((contas: Contas[]) => {
            if (!Array.isArray(contas)) {
                return contas;
            }

            return contas.filter((conta) => {
                if (!conta) {
                    return false;
                }

                const nome = (conta.nome || '').toLowerCase();
                const email = (conta.email || '').toLowerCase();

                return nome.includes(normalisedSearch) || email.includes(normalisedSearch);
            });
        });
    }
}
